/**
 * 어노테이션(그리기 메모) 오버레이 컴포넌트
 * - 사진 위에 그리기 메모를 SVG로 렌더링
 * - 점검 상세, 보고서 미리보기, 보고서 프린트에서 공통 사용
 */
import { useQuery } from '@tanstack/react-query';
import { getPhotoAnnotation, AnnotationStroke } from '../../../lib/inspections';

interface AnnotationOverlayProps {
  /** 조직 ID */
  companyId: string;
  /** 점검 ID */
  inspectionId: string;
  /** 사진 ID */
  photoId: string;
  /** 컨테이너 너비 (px) */
  containerWidth: number;
  /** 컨테이너 높이 (px) */
  containerHeight: number;
  /** X 오프셋 (px) - object-contain으로 인한 여백 */
  offsetX?: number;
  /** Y 오프셋 (px) - object-contain으로 인한 여백 */
  offsetY?: number;
  /** 표시 여부 (기본: true) */
  visible?: boolean;
  /** 프린트 모드 여부 (기본: false) */
  printMode?: boolean;
  /** 어노테이션 데이터 직접 전달 (쿼리 대신 사용) */
  strokes?: AnnotationStroke[];
}

/**
 * 어노테이션 오버레이
 *
 * 사용 예시:
 * ```tsx
 * <AnnotationOverlay
 *   companyId={orgId}
 *   inspectionId={inspId}
 *   photoId={photoId}
 *   containerWidth={500}
 *   containerHeight={400}
 *   visible={showAnnotation}
 * />
 * ```
 */
export function AnnotationOverlay({
  companyId,
  inspectionId,
  photoId,
  containerWidth,
  containerHeight,
  offsetX = 0,
  offsetY = 0,
  visible = true,
  printMode = false,
  strokes: directStrokes,
}: AnnotationOverlayProps) {
  // 어노테이션 데이터 로드 (직접 전달되지 않은 경우에만)
  const { data: annotationData } = useQuery({
    queryKey: ['photoAnnotation', inspectionId, photoId],
    queryFn: () => getPhotoAnnotation(companyId, inspectionId, photoId),
    enabled: visible && !directStrokes && !!companyId && !!inspectionId && !!photoId,
    staleTime: 30000,
  });

  // 표시할 스트로크 결정
  const strokes = directStrokes || annotationData?.data?.strokes || [];

  // 렌더링 조건 체크
  // print 모드에서는 containerWidth/Height가 0이어도 렌더링 (퍼센트 기반)
  if (!visible || strokes.length === 0) {
    return null;
  }
  if (!printMode && (containerWidth === 0 || containerHeight === 0)) {
    return null;
  }

  // 프린트 모드용 스타일
  const printStyle = printMode ? {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
  } : {
    position: 'absolute' as const,
    top: offsetY,
    left: offsetX,
    width: containerWidth,
    height: containerHeight,
    pointerEvents: 'none' as const,
  };

  // viewBox 설정
  const viewBox = printMode
    ? '0 0 100 100'
    : `0 0 ${containerWidth} ${containerHeight}`;

  return (
    <svg
      className="annotation-overlay"
      style={printStyle}
      viewBox={viewBox}
      preserveAspectRatio={printMode ? 'none' : 'xMidYMid meet'}
    >
      {strokes.map((stroke, idx) => {
        if (stroke.points.length < 2) return null;

        // 경로 데이터 생성
        const pathData = stroke.points
          .map((point, i) => {
            const cmd = i === 0 ? 'M' : 'L';
            if (printMode) {
              // 프린트 모드: 퍼센트 기반
              return `${cmd} ${point.x * 100} ${point.y * 100}`;
            } else {
              // 미리보기 모드: 픽셀 기반
              const x = point.x * containerWidth;
              const y = point.y * containerHeight;
              return `${cmd} ${x} ${y}`;
            }
          })
          .join(' ');

        // 선 두께 계산
        // stroke.width는 픽셀 값으로 저장됨 (기본 4)
        // 미리보기 모드: viewBox와 SVG 크기가 같으므로 픽셀 값 그대로 사용
        // 프린트 모드: non-scaling-stroke로 실제 픽셀로 렌더링되므로 그대로 사용
        const strokeWidth = stroke.width;

        return (
          <path
            key={idx}
            d={pathData}
            fill="none"
            stroke={stroke.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect={printMode ? 'non-scaling-stroke' : undefined}
          />
        );
      })}
    </svg>
  );
}

export default AnnotationOverlay;
